/**
 * <s-floating-toolbar> — Floating filter + sort pill that appears once the shopper
 * scrolls past its parent toolbar ([data-filter-toolbar] ancestor).
 *
 * Required markup:
 *   closest('[data-filter-toolbar]')       — The in-page toolbar it mirrors.
 *   toolbar select[name="sort_by"]          — The real sort field submitted with the form.
 *   [data-floating-sort] inside this element — Unnamed mirror select; changes are copied
 *                                              to the real select and a bubbling change fires.
 * Sets [data-visible] and toggles inert on itself.
 */
class SFloatingToolbar extends HTMLElement {
  #observer = null;
  #controller = null;

  connectedCallback() {
    const toolbar = this.closest('[data-filter-toolbar]');
    if (!toolbar) return;
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#setVisible(false);

    this.#observer = new IntersectionObserver(([entry]) => {
      this.#setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    });
    this.#observer.observe(toolbar.querySelector('[data-filter-toolbar-row]') ?? toolbar);

    // Filter & sort drawer grows out of whichever button opened it (top toolbar or floating pill)
    // and shrinks back into it on close (the drawer's closed transform maps it onto the button's box).
    for (const opener of toolbar.querySelectorAll('button[command="show-modal"][commandfor]')) {
      opener.addEventListener('click', () => {
        const dialog = opener.commandForElement ?? opener.ownerDocument.getElementById(opener.getAttribute('commandfor'));
        if (!dialog || dialog.open || getComputedStyle(dialog).direction === 'rtl') return;
        const box = opener.getBoundingClientRect();
        const width = window.innerWidth < 480 ? window.innerWidth : 480;
        const height = window.innerHeight;
        dialog.style.transformOrigin = '0 0';
        dialog.style.setProperty(
          '--panel-closed-transform',
          `translate(${box.left}px, ${box.top}px) scale(${box.width / width}, ${box.height / height})`
        );
        dialog.addEventListener('close', () => {
          dialog.style.removeProperty('transform-origin');
          dialog.style.removeProperty('--panel-closed-transform');
        }, { once: true, signal });
      }, { signal });
    }

    const mirror = this.querySelector('[data-floating-sort]');
    const realSort = () => toolbar.querySelector('select[name="sort_by"]');
    if (mirror) {
      mirror.addEventListener('change', () => {
        const real = realSort();
        if (!real) return;
        real.value = mirror.value;
        real.dispatchEvent(new Event('change', { bubbles: true }));
      }, { signal });
      toolbar.addEventListener('change', (event) => {
        if (event.target === realSort()) mirror.value = event.target.value;
      }, { signal });
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#controller?.abort();
  }

  #setVisible(visible) {
    this.toggleAttribute('data-visible', visible);
    this.inert = !visible;
  }
}

if (!customElements.get('s-floating-toolbar')) customElements.define('s-floating-toolbar', SFloatingToolbar);
